import { firstValueFrom, Subscription } from 'rxjs';
import { DraftSaver } from '@shared/services/draft-saver';
import { IOfficeHours } from 'app/models/licenses/office-hours';
import { animate, style, transition, trigger } from '@angular/animations';
import {Component, Input, OnChanges, OnDestroy, SimpleChanges, ViewChild, inject
} from '@angular/core';
import { AbstractControl, FormControl, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { SharedService } from '@shared/services/shared.service';
import { IHospitalServiceLocation } from 'app/models/licenses/hospital/hospital-service-location';
import { HospitalServiceLocationService } from 'app/services/license/hospital/hospital-service-location.service';

@Component({
  selector: 'app-hospital-service-location',
  templateUrl: './hospital-service-location.component.html',
  styleUrls: ['./hospital-service-location.component.scss'],
  animations: [
    trigger('slideRight', [
      transition(':enter', [
        style({ transform: 'translateX(100%)' }),
        animate('200ms ease-in', style({ transform: 'translateX(0%)' }))
      ]),
      transition(':leave', [
        animate('200ms ease-in', style({ transform: 'translateX(100%)' }))
      ])
    ])
  ]
})

export class HospitalServiceLocationComponent implements OnChanges, OnDestroy {
  @Input() entityId = 0;
  @Input() entityType = '';
  @Input() showType = true;
  @Input() canEdit = false;

  @ViewChild(MatPaginator) set paginator(paginator: MatPaginator) {
    if (paginator) {
      this.dataSource.paginator = paginator;
    }
  }

  @ViewChild(MatSort) set sort(sort: MatSort) {
    if (sort) {
      this.dataSource.sort = sort;
    }
  }

  public readonly dataSource = new MatTableDataSource<IHospitalServiceLocation>([]);
  public readonly locationForm = new FormGroup({
    floor: new FormControl('', { nonNullable: true, validators: Validators.required }),
    suite: new FormControl('', { nonNullable: true, validators: Validators.required }),
    servicesProvided: new FormControl('', { nonNullable: true, validators: Validators.required }),
    typeOutpatient: new FormControl(false),
    typePsychiatric: new FormControl(false),
    typeEmergency: new FormControl(false)
  }, { validators: this.atLeastOneTypeValidator() });

  public saver?: DraftSaver;
  private changes?: Subscription;
  private creating?: Promise<void>;
  public initialHours: IOfficeHours | null = null;

  ngOnDestroy(): void { this.saver?.dispose(); this.changes?.unsubscribe(); }

  hoursChanged(hours: IOfficeHours): void {
    this.location.officeHours = hours;
    this.saver?.changed();
  }

  async savePending(): Promise<boolean> {
    await this.creating;
    return this.saver ? this.saver.flush() : true;
  }

  async closeWithSave(): Promise<void> {
    if (this.isSaving) return;
    this.isSaving = true;
    try {
      if (await this.savePending()) this.closeEditor();
    } finally { this.isSaving = false; }
  }

  public entity: string = 'HospitalServiceLocation';
  public includeArchived = false;
  public isLoading = false;
  public isSaving = false;
  public managingLocation = false;
  public location: IHospitalServiceLocation = this.getEmptyLocation();

  private readonly locationService = inject(HospitalServiceLocationService);
  private readonly sharedService = inject(SharedService);

  public get displayedColumns(): string[] {
    const columns = ['floor', 'suite'];
    if (this.showType) {
      columns.push('type');
    }
    return [...columns, 'servicesProvided', 'archived'];
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.entityId || changes.entityType) {
      this.closeEditor();
      this.loadLocations(this.includeArchived);
    }
  }

  public loadLocations(includeArchived: boolean): void {
    this.includeArchived = includeArchived;

    if (!this.entityId || !this.entityType) {
      this.isLoading = false;
      this.dataSource.data = [];
      return;
    }

    this.isLoading = true;
    this.locationService.getHospitalServiceLocations(
      this.entityId,
      this.entityType,
      this.includeArchived
    ).subscribe({
      next: locations => {
        this.dataSource.data = locations;
      },
      error: error => {
        this.isLoading = false;
        this.sharedService.toastr.error('Could not load hospital service locations');
        console.error('Error loading hospital service locations', error);
      },
      complete: () => {
        this.isLoading = false;
      }
    });
  }

  public async addLocation(): Promise<void> {
    if (!this.canEdit || this.isSaving || this.creating) return;
    this.isSaving = true;
    this.creating = (async () => {
      try {
        const location = await firstValueFrom(this.locationService.createHospitalServiceLocation(
          this.getEmptyLocation(), this.entityId, this.entityType));
        if (!location.id) throw new Error('Location creation failed');
        this.dataSource.data = [...this.dataSource.data, location];
        this.openEditor(location);
      } catch (error) {
        this.handleSaveError('Could not create service location. Please try again.', error);
      } finally { this.isSaving = false; }
    })();
    await this.creating;
    this.creating = undefined;
  }

  public editLocation(location: IHospitalServiceLocation): void {
    if (this.isSaving) return;
    this.openEditor({ ...location });
  }

  public closeEditor(): void {
    this.saver?.dispose();
    this.saver = undefined;
    this.changes?.unsubscribe();
    this.managingLocation = false;
    this.isSaving = false;
    this.location = this.getEmptyLocation();
    this.locationForm.reset();
  }

  parseTypes(typeString: string): string[] {
    if (!typeString) {
      return [];
    }
    try {
      const parsed = JSON.parse(typeString);
      return Array.isArray(parsed) ? parsed : [typeString];
    } catch {
      return [typeString];
    }
  }

  formatTypeForDisplay(typeString: string): string {
    const types = this.parseTypes(typeString);
    return types.join(', ');
  }

  getSelectedTypesAsJson(): string {
    const types: string[] = [];

    if (this.locationForm.get('typeOutpatient')?.value) {
      types.push('Outpatient Satellite');
    }
    if (this.locationForm.get('typePsychiatric')?.value) {
      types.push('Psychiatric Satellite');
    }
    if (this.locationForm.get('typeEmergency')?.value) {
      types.push('Emergency Medical Services Satellite');
    }

    return types.length > 0 ? JSON.stringify(types) : '';
  }

  public async saveLocation(): Promise<void> {
    await this.closeWithSave();
  }

  private async writeDraft(): Promise<void> {
    if (!this.canEdit || !this.location.id) throw new Error('Location is not editable');
    const form = this.locationForm.getRawValue();
    const payload: IHospitalServiceLocation = {
      ...this.location, floor: form.floor.trim(), suite: form.suite.trim(),
      servicesProvided: form.servicesProvided.trim(),
      type: this.showType ? this.getSelectedTypesAsJson() : '',
      officeHours: this.location.officeHours ? { ...this.location.officeHours } : undefined
    };
    const saved = await firstValueFrom(this.locationService.saveDraft(payload));
    if (!saved.id) throw new Error('Location save failed');
    // Preserve edits made during the request; only merge server-assigned metadata.
    this.location.id = saved.id;
    this.location.isDraft = saved.isDraft;
    if (this.location.officeHours && saved.officeHours) this.location.officeHours.id = saved.officeHours.id;
    this.dataSource.data = this.dataSource.data.map(item => item.id === saved.id ? saved : item);
  }

  public archiveLocation(location: IHospitalServiceLocation, archive: boolean): void {
    const action = archive ? 'Archive' : 'Un-archive';
    this.sharedService.openConfirm(`${action} this hospital service location?`);
    this.sharedService.confirmed().subscribe(async confirmed => {
      if (!confirmed) {
        return;
      }

      if (!await this.savePending()) return;
      this.isSaving = true;
      this.locationService.archiveHospitalServiceLocation(location.id, archive).subscribe({
        next: archived => {
          if (!archived) {
            this.isSaving = false;
            this.sharedService.toastr.error(`Could not ${action.toLowerCase()} hospital service location`);
            return;
          }

          this.sharedService.toastr.success(`Hospital service location ${archive ? 'archived' : 'restored'}`);
          this.closeEditor();
          this.loadLocations(this.includeArchived);
        },
        error: error => {
          this.isSaving = false;
          this.sharedService.toastr.error(`Could not ${action.toLowerCase()} hospital service location`);
          console.error('Error changing hospital service location archive status', error);
        }
      });
    });
  }

  public deleteLocation(location: IHospitalServiceLocation): void {
    this.sharedService.openConfirm('Permanently delete this hospital service location?');
    this.sharedService.confirmed().subscribe(async confirmed => {
      if (!confirmed) {
        return;
      }

      this.isSaving = true;
      await this.saver?.stop();
      this.locationService.deleteHospitalServiceLocation(location.id).subscribe({
        next: deleted => {
          if (!deleted) {
            this.isSaving = false;
            this.resumeSaving();
            this.sharedService.toastr.error('Could not delete hospital service location');
            return;
          }

          this.dataSource.data = this.dataSource.data.filter(existing => existing.id !== location.id);
          this.sharedService.toastr.success('Hospital service location deleted');
          this.closeEditor();
        },
        error: error => {
          this.isSaving = false;
          this.resumeSaving();
          this.sharedService.toastr.error('Could not delete hospital service location');
          console.error('Error deleting hospital service location', error);
        }
      });
    });
  }

  private openEditor(location: IHospitalServiceLocation): void {
    this.saver?.dispose();
    this.changes?.unsubscribe();
    this.location = { ...location, officeHours: location.officeHours ? { ...location.officeHours } : this.emptyHours() };
    this.initialHours = { ...this.location.officeHours! };
    const types = this.parseTypes(location.type);

    this.locationForm.reset({
      floor: location.floor,
      suite: location.suite,
      typeOutpatient: types.includes('Outpatient Satellite'),
      typePsychiatric: types.includes('Psychiatric Satellite'),
      typeEmergency: types.includes('Emergency Medical Services Satellite'),
      servicesProvided: location.servicesProvided
    });
    this.managingLocation = true;
    if (location.archived || !this.canEdit) this.locationForm.disable({ emitEvent: false });
    else this.locationForm.enable({ emitEvent: false });
    this.startSaving();
  }

  async stopSaving(): Promise<void> { await this.creating; await this.saver?.stop(); }
  resumeSaving(): void { if (this.managingLocation) { this.startSaving(); this.saver?.changed(); } }

  private startSaving(): void {
    this.changes?.unsubscribe();
    this.saver = new DraftSaver(() => this.writeDraft(),
      () => this.sharedService.toastr.error('Could not save service location and hours. Your edits are still here.'));
    this.changes = this.locationForm.valueChanges.subscribe(() => this.saver?.changed());
  }

  private emptyHours(): IOfficeHours {
    return { id: 0, licenseId: '', sunday: '', monday: '', tuesday: '', wednesday: '', thursday: '', friday: '', saturday: '' };
  }

  private getEmptyLocation(): IHospitalServiceLocation {
    return {
      id: 0,
      floor: '',
      suite: '',
      servicesProvided: '',
      type: '',
      archived: false
    };
  }

  private handleSaveError(message: string, error?: unknown): void {
    this.isSaving = false;
    this.sharedService.toastr.error(message);
    if (error) {
      console.error(message, error);
    }
  }

  private atLeastOneTypeValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if(!this.showType) return null;
      const formGroup = control as FormGroup;
      const hasAtLeastOne = formGroup?.get('typeOutpatient')?.value ||
                            formGroup?.get('typePsychiatric')?.value ||
                            formGroup?.get('typeEmergency')?.value;
      return hasAtLeastOne ? null : { noTypeSelected: true };
    };
  }
}
